// The two chore mini-games, in the shell's own markup and scored by the sim's
// own functions (`sim/minigames.ts`).
//
// The old panel in src/ui/minigamePanel.ts (now deleted) was a chooser over the same two games
// in desktop-era markup. It is not loaded in the v2 route, so the surface is
// built here and the sim stays the single source of the rules and the reward.

import type { ChoreId } from '../../core/types';
import { t } from '../../i18n';
import { button, div, h } from '../dom';
import { openSheet } from '../components/sheet';
import {
  buildEggWashRound,
  canPlay,
  EGG_GRADES,
  scoreEggWash,
  scoreFeedMix,
  type EggGrade,
  type FeedChoice,
  type MinigameId,
} from '../../sim/minigames';
import { summarizeFacilities } from '../../sim/welfare';
import { completeChore } from '../actions';
import { FEED_OPTIONS } from '../selectors';
import type { ScreenContext } from '../router';

export function openMinigameSheet(
  ctx: ScreenContext<HTMLElement>,
  id: MinigameId,
  chore: ChoreId,
): void {
  const { store, shell } = ctx.services;
  const state = store.getState();
  if (!canPlay(state, id)) {
    shell.toast(t('minigame.doneToday'));
    return;
  }
  const sheet = openSheet(shell, {
    title: t(`minigame.name.${id}`),
    onClose: () => undefined,
  });

  if (id === 'feed_mix') {
    buildFeedMix(ctx, sheet.body, sheet.close, chore);
  } else {
    buildEggWash(ctx, sheet.body, sheet.close, chore);
  }
}

function buildFeedMix(
  ctx: ScreenContext<HTMLElement>,
  body: HTMLElement,
  close: () => void,
  chore: ChoreId,
): void {
  const { store } = ctx.services;
  body.appendChild(h('p', { class: 'cf-sub', text: t('minigame.feedMixPrompt') }));
  const row = div({ class: 'cf-row-grid cf-grid-3' });
  for (const option of FEED_OPTIONS) {
    row.appendChild(
      button(
        {
          class: 'cf-choice',
          on: {
            click: () => {
              let score = { correct: false, reward: 0, explanation: '' };
              store.mutate((s) => {
                score = scoreFeedMix(s, option.type as FeedChoice);
              });
              // The chore the game was for still has to be done; the minigame is
              // the bonus, never the gate (REFOCUS §10).
              completeChore(store, chore);
              body.replaceChildren(
                h('p', {
                  class: 'cf-sub',
                  text: t(score.correct ? 'minigame.feedMixRight' : 'minigame.feedMixWrong', {
                    answer: t(`feed.option.${score.explanation}`),
                  }),
                }),
                h('p', { class: 'cf-sub', text: t('minigame.reward', { reward: score.reward }) }),
                button({ class: 'cf-btn cf-btn--primary', text: t('common.done'), on: { click: close } }),
              );
            },
          },
        },
        h('span', { class: 'cf-choice__title', text: option.label }),
        h('span', { class: 'cf-choice__sub', text: option.detail }),
      ),
    );
  }
  body.appendChild(row);
}

/**
 * Egg Wash & Grade: sort the eggs actually in the nest boxes, and grade a
 * cracked egg as not sellable. The mistake the game exists to teach (sim/
 * minigames.ts).
 */
function buildEggWash(
  ctx: ScreenContext<HTMLElement>,
  body: HTMLElement,
  close: () => void,
  chore: ChoreId,
): void {
  const { store } = ctx.services;
  const facilities = summarizeFacilities(store.getState());
  const eggs = facilities.nestBoxes.flatMap((nest) => nest.state.eggs ?? []);
  if (eggs.length === 0) {
    body.appendChild(h('p', { class: 'cf-sub', text: t('minigame.eggWashEmpty') }));
    body.appendChild(
      button({
        class: 'cf-btn cf-btn--primary',
        text: t('common.close'),
        on: { click: () => completeChore(store, chore) || close() },
      }),
    );
    return;
  }

  const round = buildEggWashRound(eggs);
  const answers: Record<string, EggGrade | 'cracked'> = {};
  for (const egg of round.eggs) answers[egg.id] = egg.size;

  const list = div({ class: 'cf-stack' });
  const render = (): void => {
    list.replaceChildren();
    for (const egg of round.eggs) {
      const row = div({ class: 'cf-card cf-stack cf-stack--tight' });
      row.appendChild(h('span', { class: 'cf-small', text: t('minigame.eggWashEgg') }));
      const grades = div({ class: 'cf-row-grid cf-grid-3' });
      for (const grade of [...EGG_GRADES, 'cracked'] as Array<EggGrade | 'cracked'>) {
        grades.appendChild(
          button({
            class: 'cf-chipbtn',
            text: grade === 'cracked' ? t('minigame.eggWashCracked') : grade,
            attrs: { 'aria-pressed': String(answers[egg.id] === grade) },
            on: {
              click: () => {
                answers[egg.id] = grade;
                render();
              },
            },
          }),
        );
      }
      row.appendChild(grades);
      list.appendChild(row);
    }
  };
  render();
  body.appendChild(list);

  body.appendChild(
    button({
      class: 'cf-btn cf-btn--primary',
      text: t('minigame.eggWashSubmit'),
      on: {
        click: () => {
          let result = { correct: 0, total: round.eggs.length, reward: 0 };
          store.mutate((s) => {
            result = scoreEggWash(s, round, answers);
          });
          completeChore(store, chore);
          body.replaceChildren(
            h('p', {
              class: 'cf-sub',
              text: t('minigame.eggWashResult', { correct: result.correct, total: result.total }),
            }),
            h('p', { class: 'cf-sub', text: t('minigame.reward', { reward: result.reward }) }),
            button({ class: 'cf-btn cf-btn--primary', text: t('common.done'), on: { click: close } }),
          );
        },
      },
    }),
  );
}
